import React from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    IconButton,
    Box,
    Typography,
    Slide,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import CircleIcon from '@mui/icons-material/Circle'; // Red dot for unread notifications
import { formatDistanceToNow, isToday, isYesterday } from 'date-fns';
import { getNotifications, markAsReadNotification } from '../../../Services/authService';
import { useThemeContext } from '../../../Context/ThemeContext';
import { DARKCOLORS, LIGHTCOLORS } from '../../../Utils/constants';
import SectionLoader from '../../Common/SectionLoader';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { formatTime, formatDateTime } from '../../Common/dateFormat';
import useAuth from '../../../Hooks/useAuth';

const Transition = React.forwardRef(function Transition(props, ref) {
    return <Slide direction="down" ref={ref} {...props} timeout={500} />;
});

const NotificationsModal = ({ open, onClose, onNotificationRead }) => {
    const navigate = useNavigate();
    const { mode } = useThemeContext();
    const { t } = useTranslation();
    const { user } = useAuth();
    const isSuperAdmin = user?.roles?.includes("ROLE_SUPER_ADMIN");
    const modalBackgroundColor = mode === 'dark' ? DARKCOLORS.BackgroundCard : LIGHTCOLORS.BackgroundCard;
    const divider = mode === 'dark' ? DARKCOLORS.GreyScale40 : LIGHTCOLORS.GreyScale40;
    const textColor = mode === 'dark' ? DARKCOLORS.BodyLightBackground : LIGHTCOLORS.BodyLightBackground;
    const dangerColor = mode === 'dark' ? DARKCOLORS.Danger500 : LIGHTCOLORS.Danger500;

    const [notificationsData, setNotificationsData] = React.useState([]);
    const [loading, setLoading] = React.useState(false);

    const fetchNotifications = async () => {
        try {
            setLoading(true);
            const data = await getNotifications();
            setNotificationsData(data);
        } catch (error) {
            console.error(t("navbar.errorFetchingNotifications"), error);
        } finally {
            setLoading(false);
        }
    };

    React.useEffect(() => {
        if (open) {
            fetchNotifications();
        }
    }, [open]);

    // Function to format time based on standard notification formats
    const formatNotificationTime = (createdAt) => {
        const date = new Date(createdAt);
        if (isToday(date)) {
            return formatDistanceToNow(date, { addSuffix: true });
        } else if (isYesterday(date)) {
            return `Yesterday at ${formatTime(createdAt)}`;
        } else {
            return formatDateTime(createdAt);
        }
    };

    const handleRedirect = async (notification) => {
        await markAsReadNotification(notification.id);
        // Notify Navbar about the change
        if (onNotificationRead) {
            onNotificationRead();
        }
        if (notification.page) {
            onClose()

            const title = notification.title?.toLowerCase() || '';
            const state = { referenceId: notification.referenceId };

            // Machine-related notifications
            if (notification.page === 'machines')
                navigate(`/machine-details/${notification.referenceId}`);
            // Advertisement notifications
            else if (notification.page === 'advertisements')
                navigate(`/add-advertisement`, { state });
            // Tax report notifications
            else if (notification.page === 'taxReport')
                navigate(`/taxes`);
            // Service alert notifications — super admins go straight to the complaints page (no modal)
            else if (title.includes('service alert') || notification.page === 'service-request')
                navigate(isSuperAdmin ? `/complaints` : `/service-request`, { state });
            // Discount notifications
            else if (title.includes('discount expiring') || notification.page === 'discounts')
                navigate(`/discounts`, { state });
            // Membership card / customer notifications
            else if (title.includes('membership-card-low') || notification.page === 'customers')
                navigate(`/customers`, { state });
            else
                navigate(`/${notification.page}`, { state });
        }
    };

    const buildRestructuredMessage = (notif) => {
        const title = notif.title?.toLowerCase() || '';

        if (title.includes("advertisement expiring")) {
            return `${notif?.name || 'advertisement'}: ${t('notification.expiringAd')}`;
        } else if (title.includes("discount expiring")) {
            return `${notif?.name || 'Discount'}: ${t('notification.expiringDis')}`;
        } else if (title.includes("low stock")) {
            return `${notif?.name || 'Location'}: ${t('notification.lowStock')}`;
        } else if (title.includes("restock")) {
            return `${notif?.name || 'Location'}: ${t('notification.restockNeeded')}`;
        } else if (title.includes("empty")) {
            return `${notif?.name || 'Location'}: ${t('notification.empty')}`;
        } else if (title.includes("membership-card-low")) {
            return `${notif?.name || 'Member'}: ${t('notification.lowBalance')}`;
        } else if (title.includes("transaction-failed")) {
            return `${notif?.name || 'Transaction'}: ${t('notification.transactionFailed')}`;
        } else if (title.includes("service alert")) {
            return `${notif?.name || 'Franchise'}: ${t('notification.serviceAlert')}`;
        } else if (title.includes("tax report")) {
            return t('notification.taxReport');
        } else if (title.includes("inactive")) {
            return `${notif?.name || 'Inactive'}: ${t('notification.inactive')}`;
        } else {
            return notif?.name || notif.message;
        }
    };

    const buildRestructuredTitle = (notif) => {
        const title = notif.title?.toLowerCase() || '';

        if (title.includes("advertisement expiring")) {
            return t('notification.expiringAdTitle');
        } else if (title.includes("discount expiring")) {
            return t('notification.expiringDisTitle');
        } else if (title.includes("low stock")) {
            return t('notification.lowStockTitle');
        } else if (title.includes("restock")) {
            return t('notification.restockNeededTitle');
        } else if (title.includes("empty")) {
            return t('notification.emptyTitle');
        } else if (title.includes("membership-card-low")) {
            return t('notification.lowBalanceTitle');
        } else if (title.includes("transaction-failed")) {
            return t('notification.transactionFailedTitle');
        } else if (title.includes("service alert")) {
            return t('notification.serviceAlertTitle');
        } else if (title.includes("tax report")) {
            return `${notif?.name || ''} ${t('notification.taxReportTitle')}`;
        } else if (title.includes("inactive")) {
            return t('notification.inactiveTitle');
        }  else {
            return notif.title || notif.message;
        }
    };

    return (
        <Dialog
            open={open}
            onClose={onClose}
            fullWidth
            maxWidth="sm"
            TransitionComponent={Transition}
            keepMounted
            PaperProps={{
                sx: {
                    position: 'absolute',
                    top: 55,
                    right: 10,
                    margin: 0,
                    maxWidth: "30rem",

                    borderRadius: 3,
                    background: modalBackgroundColor,
                    boxShadow: 5,
                    "@media (max-width: 427px)": {
                        right: 5,
                        maxWidth: "none",
                        width: "95%"
                    },
                },
            }}
        >
            <DialogTitle sx={{
                display: 'flex', justifyContent: 'space-between', alignItems: 'center', p: {
                    xs: 2,  // base padding
                    sm: 3,  // small screens
                    md: "20px 24px ",
                }
            }}>
                <span className='franchise-heading-text'>{t('navbar.Notifications')}</span>
                <IconButton onClick={onClose}>
                    <CloseIcon />
                </IconButton>
            </DialogTitle>

            <DialogContent sx={{
                overflowY: 'auto', minHeight: "10rem", p: {
                    xs: 2,  // base padding
                    sm: 3,  // small screens
                    md: "20px 24px ",
                }
            }}>
                {loading ? (
                    <Box display="flex" justifyContent="center" alignItems="center" height="100%">
                        <SectionLoader />
                    </Box>
                ) : (
                    <Box>
                        {notificationsData.length > 0 ? (
                            notificationsData.map((notif, index) => (
                                <Box
                                    key={index}
                                    sx={{
                                        display: "flex",
                                        flexDirection: "column",
                                        padding: "10px",
                                        borderBottom: `1px solid ${divider}`,
                                        cursor: "pointer"
                                    }}
                                    onClick={() => handleRedirect(notif)}
                                >
                                    {/* Unread Indicator, Title, and Time */}
                                    <Box display="flex" alignItems="center" justifyContent="space-between">
                                        <Box display="flex" alignItems="center">
                                            {notif.status === "UNREAD" && (
                                                <CircleIcon sx={{ fontSize: 10, color: dangerColor, marginRight: 1 }} />
                                            )}
                                            <Typography variant="body1" id="notify-subtitle" className='popup-text1 '>
                                                {buildRestructuredTitle(notif)}
                                            </Typography>
                                        </Box>
                                        <Typography className='add-machine notification-webite-view' sx={{ fontSize: "12px !important", color: textColor }}>
                                            {formatNotificationTime(notif.createdAt)}
                                        </Typography>
                                    </Box>

                                    {/* Message */}
                                    <Typography variant="body2" className='popup-text' sx={{ marginTop: "2px", color: textColor }}>
                                        {buildRestructuredMessage(notif)}
                                    </Typography>
                                    <Box className="notification-mobile-view" sx={{ width: "100%", display: 'flex', }}>
                                        <Typography className='add-machine' sx={{ fontSize: "12px !important", justifySelf: 'flex-end', padding: '0px !important ', color: textColor, }}>
                                            {formatNotificationTime(notif.createdAt)}
                                        </Typography>
                                    </Box>
                                </Box>
                            ))
                        ) : (
                            <Typography variant="body2">{t('notification.Nonoti')}</Typography>
                        )}
                    </Box>
                )}
            </DialogContent>
        </Dialog>
    );
};

export default NotificationsModal;
